import {
  acquireDecodedFloorRasterTile,
  clearFloorRasterMemoryCache,
  getCachedFloorRasterTile,
  putCachedFloorRasterTile,
} from './floorRasterCache';
import {
  floorRasterTileSpecs,
  floorRasterVisualKey,
  type FloorRasterDataset,
  type FloorRasterDatasetRegistration,
  type FloorRasterDecodedTile,
  type FloorRasterTileSpec,
} from './floorRasterTypes';
import { floorRasterWorkerClient } from '../workers/floorRasterWorkerClient';

const arrayIds = new WeakMap<object, number>();
let nextArrayId = 0;

function identityOf(value: object): number {
  const known = arrayIds.get(value);
  if (known) return known;
  const id = ++nextArrayId;
  arrayIds.set(value, id);
  return id;
}

function runtimeDatasetKey(dataset: FloorRasterDataset): string {
  const bounds = dataset.bounds;
  return [
    dataset.sourceId,
    identityOf(dataset.polylines),
    dataset.polylines.length,
    dataset.labels.length,
    bounds.minX,
    bounds.minY,
    bounds.maxX,
    bounds.maxY,
    floorRasterVisualKey(dataset),
  ].join('|');
}

let activeKey = '';
let activeRegistration: FloorRasterDatasetRegistration | null = null;
let registrationPromise: Promise<FloorRasterDatasetRegistration> | null = null;
const encodedInFlight = new Map<string, Promise<import('./floorRasterTypes').FloorRasterEncodedTile>>();
let scheduledPrewarmKey = '';
let cancelScheduledPrewarm: (() => void) | null = null;

async function prepare(
  dataset: FloorRasterDataset
): Promise<FloorRasterDatasetRegistration> {
  const key = runtimeDatasetKey(dataset);
  if (activeKey === key && activeRegistration) return activeRegistration;
  if (activeKey === key && registrationPromise) return registrationPromise;

  floorRasterWorkerClient.beginGeneration();
  activeKey = key;
  activeRegistration = null;
  encodedInFlight.clear();
  registrationPromise = floorRasterWorkerClient
    .register(dataset)
    .then((registration) => {
      if (activeKey === key) activeRegistration = registration;
      return registration;
    })
    .finally(() => {
      if (activeKey === key) registrationPromise = null;
    });
  return registrationPromise;
}

async function encodedTile(
  registration: FloorRasterDatasetRegistration,
  spec: FloorRasterTileSpec
) {
  const cached = await getCachedFloorRasterTile(spec.key);
  if (cached) return cached;
  const existing = encodedInFlight.get(spec.key);
  if (existing) return existing;
  const promise = floorRasterWorkerClient
    .render(registration.datasetId, [spec])
    .then(async ([tile]) => {
      if (!tile) throw new Error(`Raster worker did not return tile ${spec.key}`);
      await putCachedFloorRasterTile(tile);
      return tile;
    })
    .finally(() => encodedInFlight.delete(spec.key));
  encodedInFlight.set(spec.key, promise);
  return promise;
}

async function loadDecoded(
  registration: FloorRasterDatasetRegistration,
  specs: FloorRasterTileSpec[]
): Promise<FloorRasterDecodedTile[]> {
  const encoded = await Promise.all(specs.map((spec) => encodedTile(registration, spec)));
  return Promise.all(encoded.map(acquireDecodedFloorRasterTile));
}

async function prewarmOverview(dataset: FloorRasterDataset): Promise<void> {
  if (!floorRasterWorkerClient.isAvailable) return;
  const registration = await prepare(dataset);
  const specs = floorRasterTileSpecs(registration, 0);
  await Promise.all(specs.map((spec) => encodedTile(registration, spec)));
}

function scheduleOverviewPrewarm(dataset: FloorRasterDataset): void {
  const key = runtimeDatasetKey(dataset);
  if (scheduledPrewarmKey === key) return;
  cancelScheduledPrewarm?.();
  scheduledPrewarmKey = key;

  const run = () => {
    cancelScheduledPrewarm = null;
    void prewarmOverview(dataset).catch(() => {
      // Simulation keeps the vector floor when raster preparation is unsupported.
    });
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout: 2500 });
    cancelScheduledPrewarm = () => window.cancelIdleCallback(id);
  } else if (typeof window !== 'undefined') {
    const id = window.setTimeout(run, 250);
    cancelScheduledPrewarm = () => window.clearTimeout(id);
  }
}

function reset(): void {
  cancelScheduledPrewarm?.();
  cancelScheduledPrewarm = null;
  scheduledPrewarmKey = '';
  floorRasterWorkerClient.beginGeneration();
  activeKey = '';
  activeRegistration = null;
  registrationPromise = null;
  encodedInFlight.clear();
  clearFloorRasterMemoryCache();
}

export const floorRasterService = {
  prepare,
  loadDecoded,
  prewarmOverview,
  scheduleOverviewPrewarm,
  reset,
  runtimeDatasetKey,
};

